import { UserPreferencesSchema, type Role, type UserPreferences } from "@starsach/shared-types";

/**
 * Requirements section 13.1. Preferences come from /v1/me and are applied
 * here: the theme and motion as attributes on <html> that the CSS keys
 * off, the time zone, date format and time format through useFormat(). Nothing here is
 * sensitive, so the theme is also cached in localStorage for the first
 * paint before /v1/me answers.
 */

export const DEFAULT_PREFERENCES: UserPreferences = UserPreferencesSchema.parse({});

const THEME_CACHE_KEY = "starsach.theme";

export function detectedTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
}

export function effectiveTimeZone(p: Pick<UserPreferences, "timeZone">): string {
  return p.timeZone === "auto" ? detectedTimeZone() : p.timeZone;
}

export function resolveTheme(theme: UserPreferences["theme"]): "light" | "dark" {
  if (theme !== "system") return theme;
  return typeof window !== "undefined" && window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function resolveMotion(motion: UserPreferences["motion"]): "standard" | "reduced" {
  if (motion !== "auto") return motion;
  return typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ? "reduced" : "standard";
}

/** Writes the resolved theme and motion onto <html>. Returns a disposer that stops following the OS when "system"/"auto". */
export function applyAppearance(p: Pick<UserPreferences, "theme" | "motion">): () => void {
  const root = document.documentElement;
  const paint = () => {
    root.dataset["theme"] = resolveTheme(p.theme);
    root.dataset["motion"] = resolveMotion(p.motion);
  };
  paint();
  try {
    localStorage.setItem(THEME_CACHE_KEY, p.theme);
  } catch {
    // Private mode or a full store: the first paint just follows the OS.
  }
  const queries = [window.matchMedia?.("(prefers-color-scheme: dark)"), window.matchMedia?.("(prefers-reduced-motion: reduce)")].filter(Boolean) as MediaQueryList[];
  for (const q of queries) q.addEventListener("change", paint);
  return () => {
    for (const q of queries) q.removeEventListener("change", paint);
  };
}

/** The role-appropriate landing pages (13.1): what a user can choose to arrive at. */
export function landingPages(role: Role | undefined): Array<{ path: string; label: string }> {
  const common = [
    { path: "/", label: "Focus" },
    { path: "/journeys", label: "Journeys" },
    { path: "/flow-configuration", label: "Flow configuration" },
    { path: "/trusted-advisor", label: "Trusted Advisor" },
  ];
  return role === "internal-team"
    ? [...common, { path: "/customers", label: "Customers" }, { path: "/platform", label: "Platform" }]
    : [...common, { path: "/customers", label: "Environments" }];
}

export function isLandingPage(path: string, role: Role | undefined): boolean {
  return landingPages(role).some((l) => l.path === path);
}

/** What the cached theme says before /v1/me has answered; the inline script in index.html reads the same key. */
export function cachedTheme(): UserPreferences["theme"] {
  try {
    const t = localStorage.getItem(THEME_CACHE_KEY);
    return t === "light" || t === "dark" ? t : "system";
  } catch {
    return "system";
  }
}

/**
 * #449 - the formatters (crmarr/templates localization.md, ported from Stugum). `useFormat()` is the one way a
 * page reaches them; the lint rule in eslint.config.mjs bans toLocale*String and Intl.DateTimeFormat /
 * NumberFormat everywhere else. `locale` is the device's (undefined) unless a test pins one: the portal has one
 * language, so there is no language preference, and the words it names (month abbreviations, AM/PM) are
 * English whatever the device. `timeFormat` is the clock, `auto` when absent - never read from `dateFormat`.
 */
export type FormatPrefs = Pick<UserPreferences, "timeZone" | "dateFormat"> & { timeFormat?: UserPreferences["timeFormat"]; locale?: string };

export interface WhenOptions {
  /** Seconds as well. */
  seconds?: boolean;
}

type Value = string | number | Date;
type Parts = Record<string, string>;

/** The words inside a date (month names) are the UI's language, which is English. `en-US` says `Sep`, not `Sept`. */
const WORDS = "en-US";

const pad = (n: number) => String(n).padStart(2, "0");
const toDate = (value: Value) => (value instanceof Date ? value : new Date(value));

function parts(d: Date, timeZone: string): Parts {
  const f = new Intl.DateTimeFormat("en-US", { timeZone, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: "h23" });
  const out: Parts = {};
  for (const x of f.formatToParts(d)) out[x.type] = x.value;
  // Some engines print midnight as "24".
  if (out["hour"] === "24") out["hour"] = "00";
  return out;
}

/** The abbreviated month as it reads inside a date (`Oct`); only the word is taken, the order is the format's. */
function shortMonth(d: Date, timeZone: string): string {
  return new Intl.DateTimeFormat(WORDS, { timeZone, year: "numeric", month: "short", day: "numeric" }).formatToParts(d).find((x) => x.type === "month")?.value ?? "";
}

/** The portal's one UI language. Its hour cycle, in the device's region, is what `timeFormat: auto` follows. */
const UI_LOCALE = "en";

function deviceLanguages(): readonly string[] {
  if (typeof navigator === "undefined") return [];
  return (navigator.languages ?? [navigator.language]).filter(Boolean);
}

/**
 * The tag whose hour cycle `timeFormat: auto` follows (crmarr/templates localization.md, Stugum #941): the UI
 * language, taken in the device's region when the device speaks that language - `en` on an `en-GB` device reads
 * `en-GB` (24-hour), on an `en-US` device `en-US` (12-hour). A bare `en` never gives 24-hour on its own, and a
 * device that does not speak English lends no region. A tag that already names a region is kept.
 */
export function clockLocale(uiLocale: string | undefined, device: readonly string[] = deviceLanguages()): string {
  const ui = uiLocale || UI_LOCALE;
  if (ui.includes("-")) return ui;
  const primary = ui.toLowerCase();
  return device.find((tag) => tag.toLowerCase().split("-")[0] === primary) ?? ui;
}

/** A locale's own clock: `h11` / `h12` are 12-hour, `h23` / `h24` 24-hour. */
export function localeHourCycle(locale: string): "12h" | "24h" {
  try {
    const { hourCycle } = new Intl.DateTimeFormat(locale, { hour: "numeric" }).resolvedOptions();
    return hourCycle === "h11" || hourCycle === "h12" ? "12h" : "24h";
  } catch {
    return "24h";
  }
}

/**
 * The clock in force (#449): the chosen one, or for `auto` the UI language's hour cycle in the device's region - a
 * pinned `locale` stands in for the device. Never derived from the date format.
 */
export function resolveTimeFormat(p: Pick<FormatPrefs, "timeFormat" | "locale">): "12h" | "24h" {
  if (p.timeFormat === "12h" || p.timeFormat === "24h") return p.timeFormat;
  return localeHourCycle(clockLocale(UI_LOCALE, p.locale ? [p.locale] : deviceLanguages()));
}

/** `2:05 PM` - always AM/PM, never a locale's `a. m.` (the owner's decision, 2026-09-26). */
function clock12(x: Parts, opts: WhenOptions): string {
  const h = Number(x["hour"]);
  return `${((h + 11) % 12) + 1}:${x["minute"]}${opts.seconds ? `:${x["second"]}` : ""} ${h < 12 ? "AM" : "PM"}`;
}

function clock24(x: Parts, opts: WhenOptions): string {
  return `${pad(Number(x["hour"]))}:${x["minute"]}${opts.seconds ? `:${x["second"]}` : ""}`;
}

const clock = (x: Parts, p: FormatPrefs, opts: WhenOptions) => (resolveTimeFormat(p) === "12h" ? clock12(x, opts) : clock24(x, opts));

/** The date in the chosen format, from parts already in the right zone. `d` and `timeZone` name the month. */
function dateOf(d: Date, x: Parts, p: FormatPrefs, timeZone: string): string {
  switch (p.dateFormat) {
    case "auto":
      return new Intl.DateTimeFormat(p.locale, { timeZone, year: "numeric", month: "numeric", day: "numeric" }).format(d);
    case "iso":
      return `${x["year"]}-${x["month"]}-${x["day"]}`;
    case "us":
      return `${x["month"]}/${x["day"]}/${x["year"]}`;
    case "intl":
      return `${x["day"]}/${x["month"]}/${x["year"]}`;
    case "us-text":
      return `${shortMonth(d, timeZone)} ${Number(x["day"])}, ${x["year"]}`;
    case "intl-text":
      return `${Number(x["day"])} ${shortMonth(d, timeZone)} ${x["year"]}`;
  }
}

/**
 * An instant in the user's zone, date format and time format. The date comes from `dateFormat`, the clock from
 * `timeFormat`, for every date format. **The separator follows the clock** (the owner's decision, 2026-09-26): a
 * comma before a 12-hour time, a plain space before a 24-hour one, whatever the date format, seconds included -
 * `09/26/2026, 2:05 PM` · `09/26/2026 14:05` · `26/09/2026, 2:05 PM` · `2026-09-26, 2:05 PM` · `Sep 26, 2026 14:05`.
 * An unparseable value comes back as given.
 */
export function formatWhen(value: Value, p: FormatPrefs, opts: WhenOptions = {}): string {
  const d = toDate(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const timeZone = effectiveTimeZone(p);
  const x = parts(d, timeZone);
  return `${dateOf(d, x, p, timeZone)}${resolveTimeFormat(p) === "12h" ? ", " : " "}${clock(x, p, opts)}`;
}

/** The date alone of an instant, in the user's zone. For a calendar date (`YYYY-MM-DD`) use formatDay. */
export function formatDate(value: Value, p: FormatPrefs): string {
  const d = toDate(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const timeZone = effectiveTimeZone(p);
  return dateOf(d, parts(d, timeZone), p, timeZone);
}

/** Time of day alone, in the user's zone and time format - for events on one known day. */
export function formatTime(value: Value, p: FormatPrefs, opts: WhenOptions = {}): string {
  const d = toDate(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return clock(parts(d, effectiveTimeZone(p)), p, opts);
}

const CALENDAR_DATE = /^(\d{4})-(\d{2})-(\d{2})/;

/**
 * A calendar date - a day, not an instant - in the user's date format, never converted between zones: `2026-10-16`
 * is the 16th in Auckland and in Honolulu alike. An ISO instant is taken as its UTC date (a UTC day bucket, such as
 * the start of a range the API counted in UTC days). Anything else comes back as given.
 */
export function formatDay(value: string, p: FormatPrefs): string {
  const m = CALENDAR_DATE.exec(value);
  if (!m) return value;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  return dateOf(d, parts(d, "UTC"), p, "UTC");
}

/**
 * Month and day without the year, for chart ticks and day columns: ISO `09-14`, US `09/14`, international `14/09`,
 * US written `Sep 14`, international written `14 Sep`, auto the device's short form. A bare `YYYY-MM-DD` is a
 * calendar date and is not converted; any other value is an instant in the user's zone.
 */
export function formatMonthDay(value: Value, p: FormatPrefs): string {
  const calendar = typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);
  const d = calendar ? new Date(`${value}T00:00:00Z`) : toDate(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const timeZone = calendar ? "UTC" : effectiveTimeZone(p);
  const x = parts(d, timeZone);
  switch (p.dateFormat) {
    case "auto":
      return new Intl.DateTimeFormat(p.locale, { timeZone, month: "short", day: "numeric" }).format(d);
    case "iso":
      return `${x["month"]}-${x["day"]}`;
    case "us":
      return `${x["month"]}/${x["day"]}`;
    case "intl":
      return `${x["day"]}/${x["month"]}`;
    case "us-text":
      return `${shortMonth(d, timeZone)} ${Number(x["day"])}`;
    case "intl-text":
      return `${Number(x["day"])} ${shortMonth(d, timeZone)}`;
  }
}

/** A calendar month (`YYYY-MM`) in the date format: ISO `2026-09`, numeric `09/2026`, written `Sep 2026`. */
export function formatMonth(value: string, p: FormatPrefs): string {
  const m = /^(\d{4})-(\d{2})$/.exec(value);
  if (!m) return value;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, 1));
  switch (p.dateFormat) {
    case "auto":
      return new Intl.DateTimeFormat(p.locale, { timeZone: "UTC", month: "short", year: "numeric" }).format(d);
    case "iso":
      return value;
    case "us":
    case "intl":
      return `${m[2]}/${m[1]}`;
    case "us-text":
    case "intl-text":
      return `${shortMonth(d, "UTC")} ${m[1]}`;
  }
}

/** A count with the device's grouping (`12,345`). */
export function formatNumber(value: number, p: FormatPrefs, opts?: Intl.NumberFormatOptions): string {
  return new Intl.NumberFormat(p.locale, opts).format(value);
}

/** An amount in US dollars - every price and invoice line is USD (pricing.md). */
export function formatMoney(value: number, p: FormatPrefs, opts?: Intl.NumberFormatOptions): string {
  return new Intl.NumberFormat(p.locale, { style: "currency", currency: "USD", ...opts }).format(value);
}

const PLURALS = new Intl.PluralRules(WORDS);

/**
 * The word for a count - `plural(n, "contact", "contacts")` - chosen by Intl.PluralRules rather than `n === 1`,
 * so a second language later is a catalog, not a hunt through every sentence (localization.md decision 6).
 */
export function plural(n: number, one: string, other: string): string {
  return PLURALS.select(n) === "one" ? one : other;
}

/**
 * A `datetime-local` value (`2026-10-16T14:05`), read as a wall-clock time in `timeZone`, as the instant it names.
 * The input itself has no zone and the browser would read it in the device's, which is not the user's chosen one.
 * In a gap (spring forward) the time moves on by the gap; in an overlap it takes the earlier instant.
 */
export function wallTimeToInstant(local: string, timeZone: string): Date | undefined {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(local);
  if (!m) return undefined;
  const asUtc = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]));
  const offset = (t: number) => {
    const x = parts(new Date(t), timeZone);
    return Date.UTC(Number(x["year"]), Number(x["month"]) - 1, Number(x["day"]), Number(x["hour"]), Number(x["minute"]), Number(x["second"])) - t;
  };
  const first = asUtc - offset(asUtc);
  const second = asUtc - offset(first);
  const reads = (t: number) => t + offset(t) === asUtc;
  const exact = [first, second].filter(reads);
  return new Date(exact.length > 0 ? Math.min(...exact) : Math.max(first, second));
}
